/**
 * Bounded ERC-20 approval — the single approve implementation for every EVM money path.
 *
 * PROVENANCE: this is `approveErc20Exact`, lifted VERBATIM out of the `executeDexBridge` closure in
 * `useDexBridge.ts` (it lived at `useDexBridge.ts:616-654`) so the CCTP `bridge()` path (#137 Phase 1)
 * can reuse it instead of growing a second, less-hardened approve. The move is behaviour-preserving:
 * the only changes are that the values the closure captured are now explicit parameters —
 * `params.senderAddress` became `owner`, the `setState({currentStep:'approving'})` side effect became
 * the `onApproving` callback, and the wagmi `config` is passed in rather than imported. That last one
 * matters: `useDexBridge` binds its config from `useConfig()` (the WagmiProvider's), so importing
 * `@/config/wagmi` here would have silently swapped the object that call site uses. Every read, write,
 * comparison, retry bound and thrown message is byte-identical to the original. Do NOT "improve" it
 * here without re-reading why each guard exists.
 *
 * AMENDED BY DEV-375 (so the "byte-identical" claim above is no longer literally true, and says so
 * rather than quietly decaying): the receipt wait now goes through `waitForReceiptOrUnknown` below,
 * which calls viem's action instead of `@wagmi/core`'s wrapper and treats EVERY non-obtain outcome
 * — not only a timeout — as UNKNOWN. `useDexBridge` consumes the same export rather than keeping the
 * inlined copy that used to live at its `transferToNear1ClickDeposit`; that duplicate is how this
 * defect came to exist at two sites, so it was exported-and-DELETED rather than fixed twice.
 *
 * The hardening it carries, each earned from a real failure:
 *  - approves EXACTLY `target`, never MAX — a stale unlimited allowance is a standing drain risk
 *  - resets to 0 first for USDT-class tokens that revert on approve-over-nonzero (SafeERC20.forceApprove)
 *  - no-ops when the existing allowance already covers `target` (this is also what makes an abandoned
 *    bridge-after-approve retry cheap: the second attempt re-reads the allowance and skips the approve)
 *  - verifies the post-allowance with backoff, because a single stale RPC read-after-write once
 *    stranded a real swap whose approve had actually landed on-chain
 */
import { readContract, writeContract, type Config } from '@wagmi/core'
// DEV-375 — viem's OWN action, not @wagmi/core's wrapper. This is load-bearing, not a style choice;
// see the header comment on `waitForReceiptOrUnknown` for why the wrapper makes I1 and I2
// indistinguishable. `getAction` is imported for the same reason viem itself uses it: it honours an
// action supplied ON the client, which is the seam the tests inject a stub through.
import { waitForTransactionReceipt as viemWaitForTransactionReceipt } from 'viem/actions'
import { getAction } from 'viem/utils'
import { RECEIPT_UNKNOWN_MARKER } from './errors'

/**
 * Ceiling on waiting for an approve receipt.
 *
 * ── WHY THIS EXISTS: THE HANG IS A DEPENDENCY DEFAULT, NOT AN OVERSIGHT ──────────────────────────
 * `waitForTransactionReceipt` was called with no `timeout`, and @wagmi/core destructures
 * `timeout = 0`, OVERRIDING viem's own 180_000 default. viem then does
 * `const timer = timeout ? setTimeout(…) : undefined` — 0 is falsy, so NO timer is installed and the
 * promise can never reject. On an unknown hash viem silently continues to the next block, forever.
 *
 * That is exactly what a Safe produces: `eth_sendTransaction` returns a `safeTxHash`, which is not a
 * transaction hash and never resolves to a receipt. The user sat on "Approving — confirm in your
 * wallet" while their wallet showed the approval already executed. `burnError` stayed null, so no
 * error branch engaged: no toast, no timeout, no cancel, only reload.
 *
 * The value restores viem's own default rather than inventing one.
 */
export const RECEIPT_WAIT_TIMEOUT_MS = 180_000

/**
 * Copy for a receipt wait that ran out of time.
 *
 * The status is UNKNOWN, not failed. "Failed — try again" would be a false statement about money
 * (the approve may well have mined) and is the same false-assertion class as the refunded-funds copy
 * that had to be deleted from the DEX path. It also must not offer a one-click retry: the no-op
 * branch at the top of `approveErc20Exact` correctly skips an already-mined approve, so a retry goes
 * straight on to the value-moving call.
 */
export function receiptTimeoutMessage(what: string, hash: string, valueMoving = true): string {
  // The marker is IMPORTED, not inlined, and that is load-bearing: `classifyError` matches on it to
  // EXEMPT this string from the 100-char UNKNOWN truncation which was cutting the message off at
  // "…check your wallet or a" — losing both the hash and the closing double-submit warning, i.e.
  // exactly the two clauses this message exists to deliver. Reword around the marker freely; do not
  // edit the marker itself out, or the truncation silently comes back.
  //
  // DEV-375 finding #8 — `valueMoving`: an ERC-20 approve moves NO value, and its retry is the safe
  // no-op branch at the top of `approveErc20Exact` (I5). Dressing it as "so you don't submit it
  // twice" — copy written for a value-moving transfer — is a false claim about this call. Approve
  // sites pass `valueMoving: false` and drop that clause; every value-moving caller keeps it.
  return `Couldn't confirm the ${what} within ${Math.round(RECEIPT_WAIT_TIMEOUT_MS / 1000)}s. ` +
    `${RECEIPT_UNKNOWN_MARKER} — check your wallet or a block explorer for ${hash} before trying again` +
    (valueMoving ? `, so you don't submit it twice.` : `.`)
}

/**
 * DEV-375 — copy for a receipt read that FAILED for any reason other than running out of time.
 *
 * Deliberately makes NO claim about how long anything took (I4): the measured production failure is
 * an INSTANT `-32602 "Archive requests require a personal token"` from the configured public
 * endpoint, so "within 180s" — true of `receiptTimeoutMessage` — would be a false statement about a
 * money event here. Everything else about the two messages is the same, on purpose: the marker, the
 * hash, and the closing double-submit warning.
 *
 * ── I6(b): THIS STRING IS CHECKED AGAINST THE CLASSIFIER'S EARLIER BRANCHES, NOT JUST THE MARKER ──
 * Carrying `RECEIPT_UNKNOWN_MARKER` is necessary but NOT sufficient. `classifyError` runs
 * `isUserRejection`, `isInsufficientNativeError`, `isInsufficientFundsError`, `isChainSwitchError`
 * and `isNetworkError` BEFORE it reaches the marker check, and any of those replaces the whole
 * message with a short canned string — losing the hash and the double-submit clause, i.e. the same
 * truncation defeat the marker was introduced to close, through a different door. So this copy
 * contains none of: `timeout`, `network error`, `failed to fetch`, `network request failed`,
 * `connection refused`, `unrecognized chain`, `switch network`, `wrong chain` (case-insensitive).
 * `erc20Approve.timeout.test.ts` asserts that mechanically — reword freely, but keep it green.
 */
export function receiptUnknownMessage(what: string, hash: string, valueMoving = true): string {
  // DEV-375 finding #8 — see `receiptTimeoutMessage`'s note. Same clause, same reason.
  return `Couldn't confirm the ${what} on-chain. ${RECEIPT_UNKNOWN_MARKER} — ` +
    `check your wallet or a block explorer for ${hash} before trying again` +
    (valueMoving ? `, so you don't submit it twice.` : `.`)
}

/** True for viem/wagmi's receipt-wait timeout, which is a distinct condition from a revert. */
function isReceiptTimeout(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name ?? ''
  const message = (err as { message?: string } | null)?.message ?? ''
  return name === 'WaitForTransactionReceiptTimeoutError' || /Timed out while waiting/i.test(message)
}

/** The reasons viem reports for a receipt that belongs to a REPLACEMENT of the hash we broadcast. */
export type ReceiptReplacedReason = 'repriced' | 'cancelled' | 'replaced'

/**
 * A receipt that was actually OBTAINED, normalised to the two outcomes a caller may act on.
 *
 * `status` already folds in I9: a receipt reached through a `cancelled`/`replaced` replacement is
 * reported as `'reverted'`, because the transaction the user was told about did NOT execute. Only a
 * `repriced` replacement (same to/value/input, just a higher fee) is the same money movement and may
 * still read as success.
 */
export interface SettledReceipt {
  status: 'success' | 'reverted'
  /** The hash of the transaction the receipt is FOR — the replacement's, when one was mined (I9). */
  transactionHash: `0x${string}`
  replacedReason: ReceiptReplacedReason | null
}

/**
 * DEV-375 — wait for a receipt, and answer the ONE question that matters: did we obtain one?
 *
 * ── WHY THIS CALLS viem's ACTION AND NOT `@wagmi/core`'s WRAPPER (LOAD-BEARING) ──────────────────
 * `@wagmi/core`'s `waitForTransactionReceipt` re-reads the revert reason and THROWS a plain
 * `new Error(reason)` whenever `receipt.status === 'reverted'`
 * (`node_modules/@wagmi/core/dist/esm/actions/waitForTransactionReceipt.js:8-27`). Through that
 * wrapper, "the chain told us this reverted" and "we could not reach the chain" arrive as the same
 * shape — a throw — so no predicate downstream of it can separate I1 (never call an unconfirmed
 * transfer failed) from I2 (never soften a genuine revert to "may still be processing"). Worse, the
 * revert-reason re-read is itself two more RPC calls (`getTransaction` + `call`): on the very
 * endpoints that gate `eth_getTransactionReceipt` those fail, so a REAL revert would surface as a
 * network-shaped throw and be softened to UNKNOWN — the inverse lie.
 *
 * viem's own action `emit.resolve(receipt)` unconditionally and never inspects `status`
 * (`node_modules/viem/_esm/actions/public/waitForTransactionReceipt.js:79,131,188`), so a reverted
 * receipt is RETURNED (I2 preserved) while every non-obtain path throws (I1 enforced). That split is
 * the whole fix. Do not "simplify" this back onto the wagmi wrapper.
 *
 * ── THE PREDICATE IS "DID WE GET A RECEIPT", NOT A LIST OF ERROR NAMES ───────────────────────────
 * The defect this replaces bounded ONE error shape (`WaitForTransactionReceiptTimeoutError`) and
 * rethrew everything else, so the measured `-32602` archive gate — and every 403/429/5xx, CORS
 * failure and `TransactionReceiptNotFoundError` — fell through to a red "Transfer Failed" on a
 * transfer that had in fact delivered. Enumerating more names would only move the boundary; the
 * class is "no receipt was obtained", and every member of it is UNKNOWN.
 *
 * @param client An ALREADY-RESOLVED viem client. Callers pass `config.getClient({ chainId })`.
 *   Taking the client rather than the wagmi `Config` is what makes the seam testable: `getAction`
 *   honours `client.waitForTransactionReceipt` when present
 *   (`node_modules/viem/_esm/utils/getAction.js:13-18`), so a test injects a stub client instead of
 *   barrel-mocking `viem/actions` — which would gut the `call`/`getTransaction` that `@wagmi/core`
 *   imports from the same barrel.
 * @throws An Error whose message carries `RECEIPT_UNKNOWN_MARKER` whenever no receipt was obtained.
 */
export async function waitForReceiptOrUnknown(
  // The client shape is intentionally structural: `getClient` returns viem's fully-extended public
  // client type, and naming it here would drag viem's generics through every caller for no gain.
  client: unknown,
  { hash, timeout = RECEIPT_WAIT_TIMEOUT_MS, what, valueMoving = true }: {
    hash: `0x${string}`
    timeout?: number
    /** Human noun for the thing being confirmed, e.g. 'token approval', 'deposit transfer'. */
    what: string
    /**
     * DEV-375 finding #8 — false by construction for an approve (moves no value, no double-submit
     * risk); true (the default, preserving byte-identical copy for every existing value-moving
     * caller) for anything that actually broadcasts a transfer.
     */
    valueMoving?: boolean
  },
): Promise<SettledReceipt> {
  let replacedReason: ReceiptReplacedReason | null = null
  let receipt: { status: string; transactionHash: `0x${string}` }
  try {
    // The cast is confined to this one line and is deliberate: viem's action is generic over the
    // client's `chain`, and threading that generic outward would put viem's `Client<Transport,
    // chain>` in the signature of every caller — the coupling the structural `client: unknown`
    // parameter above exists to avoid. The runtime contract is exactly viem's own.
    const resolveReceiptAction = getAction as unknown as (
      c: unknown, fn: unknown, name: string,
    ) => (params: {
      hash: `0x${string}`
      timeout?: number
      onReplaced?: (r: { reason: ReceiptReplacedReason }) => void
    }) => Promise<{ status: string; transactionHash: `0x${string}` }>
    const action = resolveReceiptAction(client, viemWaitForTransactionReceipt, 'waitForTransactionReceipt')
    receipt = await action({
      hash,
      timeout,
      // I9. viem resolves the REPLACEMENT's receipt when it finds one, so without this callback a
      // MetaMask "cancel" (a 0-value self-send that replaces the user's transfer) yields a
      // success-status receipt while the original hash is dead — a false SUCCESS on a transfer that
      // never happened.
      onReplaced: (r: { reason: ReceiptReplacedReason }) => { replacedReason = r.reason },
    })
  } catch (err) {
    // EVERY throw is "no receipt obtained" ⇒ UNKNOWN. The only distinction kept is which copy is
    // TRUE: the timeout message asserts a duration that really did elapse, so it is preserved for
    // that one case; nothing else may claim it (I4).
    if (isReceiptTimeout(err)) throw new Error(receiptTimeoutMessage(what, hash, valueMoving))
    throw new Error(receiptUnknownMessage(what, hash, valueMoving))
  }
  // DEV-375 finding #2 — a receipt WAS obtained (no throw above), but that does not mean it carries a
  // verdict. viem emits `status: null` when a node omits the field, and viem's own `receiptStatuses`
  // map (`node_modules/viem/_esm/utils/formatters/transactionReceipt.js:5-8,34-36`) only recognises
  // `'0x0'`/`'0x1'` — anything else, `null` included, formats to `status: undefined`. The ternary below
  // treats anything that is not literally `'success'` as `'reverted'`, which turns "we never got a
  // verdict" into a false, terminal "Failed" for a transaction that may have landed — the same I1
  // breach this whole function exists to close, one field deeper. Newly reachable: this diff routes
  // every EVM receipt read through eight second-tier fallback endpoints whose receipt shapes this app
  // has never exercised.
  if (receipt.status !== 'success' && receipt.status !== 'reverted') {
    throw new Error(receiptUnknownMessage(what, hash, valueMoving))
  }
  // A replacement we did not ask for is a FAILED outcome even with a success-status receipt, because
  // the money movement the user authorised did not occur. `repriced` is the same movement.
  const replacedAway = replacedReason === 'cancelled' || replacedReason === 'replaced'
  return {
    status: receipt.status === 'success' && !replacedAway ? 'success' : 'reverted',
    // Never the hash we broadcast — the receipt's own, so what we persist is what actually mined.
    transactionHash: receipt.transactionHash ?? hash,
    replacedReason,
  }
}

/**
 * Wait for a receipt with a bounded deadline, converting every non-obtain outcome into UNKNOWN.
 * Used ONLY by the two approve/reset call sites below, so `valueMoving: false` is hardcoded rather
 * than threaded as a parameter nothing here would ever set otherwise (DEV-375 finding #8).
 */
async function waitBounded(
  config: Config,
  hash: `0x${string}`,
  chainId: number,
  what: string,
): Promise<SettledReceipt> {
  // DEV-375 finding #10 — see the matching note at `useDexBridge.ts`'s deposit-transfer call site:
  // `getClient` must not throw OUTSIDE the class this whole fix exists to enforce.
  let client: unknown
  try {
    client = config.getClient({ chainId })
  } catch {
    throw new Error(receiptUnknownMessage(what, hash, false))
  }
  return waitForReceiptOrUnknown(client, {
    hash, timeout: RECEIPT_WAIT_TIMEOUT_MS, what, valueMoving: false,
  })
}

const ALLOWANCE_ABI = [{ name: 'allowance', type: 'function', stateMutability: 'view', inputs: [{ name: 'owner', type: 'address' }, { name: 'spender', type: 'address' }], outputs: [{ name: '', type: 'uint256' }] }] as const
const APPROVE_ABI = [{ name: 'approve', type: 'function', stateMutability: 'nonpayable', inputs: [{ name: 'spender', type: 'address' }, { name: 'amount', type: 'uint256' }], outputs: [{ name: '', type: 'bool' }] }] as const

/**
 * Approve EXACTLY `target` of `tokenAddr` for `spender`, on behalf of `owner`, on `chainId`.
 *
 * @param onApproving Invoked once, immediately before the first on-chain write, ONLY when an approve
 *   is actually needed. Callers use it to move their step UI into an approving state. It is not
 *   called on the no-op path, so a sufficient existing allowance never flashes an approval step.
 */
export async function approveErc20Exact(
  wagmiConfig: Config,
  tokenAddr: `0x${string}`,
  spender: `0x${string}`,
  owner: `0x${string}`,
  target: bigint,
  chainId: number,
  onApproving?: () => void,
): Promise<void> {
  const readAllowance = () => readContract(wagmiConfig, {
    address: tokenAddr, abi: ALLOWANCE_ABI, functionName: 'allowance',
    args: [owner, spender], chainId,
  })
  const current = await readAllowance()
  if (current >= target) return
  onApproving?.()
  if (current > 0n) {
    const resetHash = await writeContract(wagmiConfig, {
      address: tokenAddr, abi: APPROVE_ABI, functionName: 'approve', args: [spender, 0n], chainId,
    })
    const resetReceipt = await waitBounded(wagmiConfig, resetHash, chainId, 'allowance reset')
    // DEV-375: this branch was UNREACHABLE until now and is not new code that happens to look wired.
    // `@wagmi/core`'s wrapper THREW on a reverted receipt, so `waitBounded` never returned one and
    // `status === 'reverted'` could not be observed here. Calling viem's action directly is what
    // makes it live — which is also why I2 needs it: a genuine revert must stay a revert.
    if (resetReceipt.status === 'reverted') {
      // DEV-375 finding #9 — mirrors `useDexBridge.ts`'s deposit-transfer branch (:1394-1401): a
      // wallet cancel/replace is normalised to `status:'reverted'` by `waitForReceiptOrUnknown` (I9),
      // but "reverted on-chain" is a claim that the chain executed and rejected the transaction. A
      // user who hit Cancel in their wallet never broadcast anything that could revert.
      if (resetReceipt.replacedReason === 'cancelled') {
        throw new Error('Token approval reset (to 0) was cancelled in your wallet before it reached the chain')
      }
      if (resetReceipt.replacedReason === 'replaced') {
        throw new Error('Token approval reset (to 0) was replaced by another transaction in your wallet before it reached the chain')
      }
      throw new Error('Token approval reset (to 0) reverted on-chain')
    }
  }
  const approveHash = await writeContract(wagmiConfig, {
    address: tokenAddr, abi: APPROVE_ABI, functionName: 'approve', args: [spender, target], chainId,
  })
  const approveReceipt = await waitBounded(wagmiConfig, approveHash, chainId, 'token approval')
  // DEV-375: reachable for the first time here too — see the note on the reset branch above. It also
  // now covers the I9 case: an approve whose hash was CANCELLED or REPLACED in the mempool is
  // normalised to 'reverted' by `waitForReceiptOrUnknown`, because the allowance was never granted.
  if (approveReceipt.status === 'reverted') {
    // DEV-375 finding #9 — same mirror as the reset branch above.
    if (approveReceipt.replacedReason === 'cancelled') {
      throw new Error('Token approval transaction was cancelled in your wallet before it reached the chain')
    }
    if (approveReceipt.replacedReason === 'replaced') {
      throw new Error('Token approval transaction was replaced by another transaction in your wallet before it reached the chain')
    }
    throw new Error('Token approval transaction reverted on-chain')
  }
  // Post-approval verification. waitForTransactionReceipt already confirmed the approve mined, so a
  // sub-target read here is almost always RPC read-after-write lag (the node serving this eth_call
  // hasn't indexed the approve's block yet) — NOT a real failure. We saw this strand a real swap:
  // the approve landed on-chain but a single stale read returned 0 → false "allowance insufficient"
  // → the whole swap aborted. Retry with backoff (~9s worst case) before giving up; only a token
  // that genuinely no-ops the approve (proxy/fee-on-transfer weirdness) stays < target.
  let post = await readAllowance()
  for (let attempt = 0; post < target && attempt < 5; attempt++) {
    await new Promise(resolve => setTimeout(resolve, 600 * (attempt + 1)))
    post = await readAllowance()
  }
  if (post < target) {
    throw new Error(`Approval confirmed but allowance insufficient (got ${post}, need ${target}). The token may require resetting approval to 0 first.`)
  }
}
